/**
 * Network particle background with animated connections
 * Inspired by modern dashboard designs with glassmorphism
 */
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  isOrange: boolean;
}

interface Bubble {
  x: number;
  y: number;
  radius: number;
  speed: number;
  opacity: number;
  hue: number;
}

const GradientBackground: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const bubblesRef = useRef<Bubble[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Configuration
    const particleCount = 80;
    const maxDistance = 150;
    const bubbleCount = 15;

    // Resize canvas to match display size
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    // Initialize particles
    const initParticles = () => {
      const rect = canvas.getBoundingClientRect();
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * rect.width,
          y: Math.random() * rect.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 3 + 2,
          isOrange: Math.random() > 0.7 // 30% orange nodes
        });
      }
    };

    // Initialize bubbles
    const initBubbles = () => {
      const rect = canvas.getBoundingClientRect();
      bubblesRef.current = [];
      for (let i = 0; i < bubbleCount; i++) {
        bubblesRef.current.push({
          x: Math.random() * rect.width,
          y: rect.height + Math.random() * 100,
          radius: Math.random() * 40 + 20,
          speed: Math.random() * 0.5 + 0.3,
          opacity: Math.random() * 0.3 + 0.2,
          hue: Math.random() * 60 + 10 // Orange hues
        });
      }
    };

    // Draw particles
    const drawParticles = () => {
      particlesRef.current.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);

        if (isDark) {
          // Dark mode colors
          if (particle.isOrange) {
            ctx.fillStyle = 'rgba(251, 146, 60, 0.8)'; // Lighter orange for dark mode
          } else {
            ctx.fillStyle = 'rgba(100, 116, 139, 0.6)'; // Gray
          }
        } else {
          // Light mode colors
          if (particle.isOrange) {
            ctx.fillStyle = 'rgba(249, 115, 22, 0.6)'; // Brand orange
          } else {
            ctx.fillStyle = 'rgba(148, 163, 184, 0.5)'; // Light gray
          }
        }

        ctx.fill();
      });
    };

    // Draw connections between nearby particles
    const drawConnections = () => {
      const particles = particlesRef.current;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.3;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);

            if (isDark) {
              ctx.strokeStyle = `rgba(251, 146, 60, ${opacity})`;
            } else {
              ctx.strokeStyle = `rgba(249, 115, 22, ${opacity})`;
            }

            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
    };

    // Update particle positions
    const updateParticles = () => {
      const rect = canvas.getBoundingClientRect();
      particlesRef.current.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > rect.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > rect.height) particle.vy *= -1;

        // Keep within bounds
        particle.x = Math.max(0, Math.min(rect.width, particle.x));
        particle.y = Math.max(0, Math.min(rect.height, particle.y));
      });
    };

    // Draw bubbles with glassmorphism effect
    const drawBubbles = () => {
      bubblesRef.current.forEach(bubble => {
        // Create radial gradient for glassmorphism effect
        const gradient = ctx.createRadialGradient(
          bubble.x,
          bubble.y,
          0,
          bubble.x,
          bubble.y,
          bubble.radius
        );

        if (isDark) {
          // Dark mode - glowing orange bubbles
          gradient.addColorStop(0, `hsla(${bubble.hue}, 90%, 65%, ${bubble.opacity * 0.4})`);
          gradient.addColorStop(0.5, `hsla(${bubble.hue}, 80%, 55%, ${bubble.opacity * 0.25})`);
          gradient.addColorStop(1, `hsla(${bubble.hue}, 70%, 45%, 0)`);
        } else {
          // Light mode - soft white/orange bubbles
          gradient.addColorStop(0, `hsla(${bubble.hue}, 70%, 85%, ${bubble.opacity * 0.5})`);
          gradient.addColorStop(0.5, `hsla(${bubble.hue}, 60%, 75%, ${bubble.opacity * 0.3})`);
          gradient.addColorStop(1, `hsla(${bubble.hue}, 50%, 65%, 0)`);
        }

        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Add subtle rim light
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.radius, 0, Math.PI * 2);
        ctx.strokeStyle = isDark
          ? `hsla(${bubble.hue}, 100%, 70%, ${bubble.opacity * 0.3})`
          : `hsla(${bubble.hue}, 80%, 90%, ${bubble.opacity * 0.4})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    };

    // Update bubble positions
    const updateBubbles = () => {
      const rect = canvas.getBoundingClientRect();
      bubblesRef.current.forEach(bubble => {
        // Float upward
        bubble.y -= bubble.speed;
        // Slight horizontal drift
        bubble.x += Math.sin(bubble.y * 0.01) * 0.5;

        // Reset bubble when it goes off screen
        if (bubble.y + bubble.radius < 0) {
          bubble.y = rect.height + bubble.radius;
          bubble.x = Math.random() * rect.width;
        }
      });
    };

    // Animation loop
    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Draw bubbles first (background layer)
      drawBubbles();
      updateBubbles();

      // Draw network particles on top
      drawConnections();
      drawParticles();
      updateParticles();

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Initialize and start animation
    resize();
    initParticles();
    initBubbles();
    animate();

    // Handle window resize
    const handleResize = () => {
      resize();
      initParticles();
      initBubbles();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDark]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: 'hidden',
        background: isDark ? '#0A0A0A' : '#F5F5F7',
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default GradientBackground;
